import { LensPlatformClient, LensSDKException, type UserBusinessRole } from "lens-platform-sdk";
import { useLensPlatformClient } from "./useLensPlatformClient";
import { useMutation } from "@tanstack/react-query";
import { queryClient } from "src/App";
import { useAnalytics } from "./useAnalytics";

type Response = Awaited<ReturnType<LensPlatformClient["business"]["createInvitation"]>>;

/**
 * Hook for creating invitations for a business.
 */
export const useCreateBusinessInvitation = (businessId?: string, onSuccess?: (response: Response) => void) => {
  const lensPlatformClient = useLensPlatformClient();
  const { track, trackError } = useAnalytics();

  const {
    isLoading: loading,
    error,
    mutate: createBusinessInvitation,
    data: response,
  } = useMutation<
    Response,
    LensSDKException,
    {
      email: string;
      subscriptionId?: string;
      role: UserBusinessRole;
    }
  >(
    async ({ email, subscriptionId, role }) => {
      if (!businessId) {
        throw new Error("Business ID is required");
      }

      const response: Response = await lensPlatformClient.business.createInvitation(
        businessId,
        email,
        subscriptionId,
        role,
      );

      if (subscriptionId) {
        track("Assigned Seats to Subscription", { subscriptionId });
      } else {
        track(`Invited ${role} to Business`);
      }

      return response;
    },
    {
      onSuccess: async (response) => {
        await queryClient.invalidateQueries({
          queryKey: ["businessInvitations", businessId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["businessSubscriptions", businessId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["businessUsers", businessId],
        });
        onSuccess?.(response);
      },
      onError: (error, { role, subscriptionId }) => {
        if (subscriptionId) {
          trackError("Assigning Seats to Subscription Failed", {
            subscriptionId,
          });
        } else {
          trackError(`${role} Invitation Failed`);
        }
      },
    },
  );

  return {
    createBusinessInvitation,
    error,
    response,
    loading,
  };
};
